import React, { useState } from "react";
import { X, Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { inputCls as inp } from "./constants";
import { TeamLogo } from "./BracketView";

export default function MatchEditor({ tid, match, logos, allowDraw, onClose, onSaved }) {
  const [sa, setSa] = useState(match.sa || 0);
  const [sb, setSb] = useState(match.sb || 0);
  const [status, setStatus] = useState(match.status);
  const [when, setWhen] = useState(match.scheduled_at || "");
  const initialWinner = match.winner ? (match.winner === match.a ? "a" : "b") : match.status === "completed" ? "draw" : null;
  const [winner, setWinner] = useState(initialWinner);
  const [busy, setBusy] = useState(false);
  const ready = ![match.a, match.b].some((x) => x === "TBD" || /^Slot \d+$/.test(x));

  const save = async () => {
    setBusy(true);
    try {
      const body = { sa: Number(sa), sb: Number(sb), status, scheduled_at: when };
      if (winner !== initialWinner) { body.set_winner = true; body.winner = winner; }
      const r = await api.put(`/manage/tournaments/${tid}/matches/${match.id}`, body);
      toast.success("Match updated");
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); } finally { setBusy(false); }
  };

  const Side = ({ side, val, set }) => (
    <div className="flex-1 text-center">
      <div className="flex flex-col items-center gap-2"><TeamLogo team={match[side]} logos={logos} size="w-12 h-12" /><div className="font-bold text-white truncate max-w-[140px]">{match[side]}</div></div>
      <input type="number" min={0} value={val} onChange={(e) => set(e.target.value)} disabled={!ready} className={`${inp} mt-3 text-center text-2xl font-head`} data-testid={`score-${side}`} />
      <button type="button" disabled={!ready} onClick={() => setWinner(winner === side ? null : side)} data-testid={`winner-${side}`}
        className={`mt-2 w-full py-1.5 rounded-lg text-xs font-bold flex items-center justify-center gap-1 transition-colors ${winner === side ? "bg-green-500/20 text-green-400" : "bg-white/5 text-white/60 hover:text-white"}`}>
        <Trophy size={12} /> {winner === side ? "Winner — advances" : "Set as winner"}
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center p-4" data-testid="match-editor">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg card-surface rounded-2xl p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={18} /></button>
        <div className="eyebrow text-white/40">{match.round_name}{match.group ? ` · Group ${match.group}` : ""}</div>
        <h3 className="text-lg font-bold text-white mb-5">Update Match</h3>
        {!ready && <div className="mb-4 text-xs text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2">Waiting for both teams (assign teams to slots first). You can still set the schedule.</div>}
        <div className="flex items-start gap-4">
          <Side side="a" val={sa} set={setSa} />
          <div className="pt-24 text-white/30 font-bold">VS</div>
          <Side side="b" val={sb} set={setSb} />
        </div>
        {allowDraw && ready && (
          <button type="button" onClick={() => setWinner(winner === "draw" ? null : "draw")} data-testid="winner-draw"
            className={`mt-3 w-full py-1.5 rounded-lg text-xs font-bold ${winner === "draw" ? "bg-white/20 text-white" : "bg-white/5 text-white/50"}`}>Draw</button>
        )}
        <div className="grid grid-cols-2 gap-3 mt-5">
          <div><label className="block text-xs font-semibold text-white/50 mb-1.5">Match status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={inp} data-testid="match-status">
              <option value="upcoming">Upcoming</option><option value="live">Live</option><option value="completed">Completed</option>
            </select></div>
          <div><label className="block text-xs font-semibold text-white/50 mb-1.5">Scheduled at</label>
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className={inp} data-testid="match-time" /></div>
        </div>
        <button onClick={save} disabled={busy} data-testid="match-save" className="mt-5 w-full py-2.5 rounded-full bg-[hsl(var(--primary))] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
          {busy && <Loader2 size={16} className="animate-spin" />} Save Match
        </button>
      </div>
    </div>
  );
}
